import * as stylex from "@stylexjs/stylex";
import { SquarePen, StickyNote as StickyNoteGlyph } from "lucide-react";
import { copy } from "@/copy";
import type { Breakpoint } from "@/domain/breakpoint";
import { type BoardNote, boardText } from "@/domain/notes";
import type { AccountSettings } from "@/domain/schemas";
import { type Clock, clockAt } from "@/domain/time";
import type { ZoneState } from "@/domain/zoneState";
import "@/theme/tokens.stylex";
import { colors, radii, space } from "@/theme/tokens.stylex";
import "@/theme/type.stylex";
import { type } from "@/theme/type.stylex";
import { Button } from "@/ui/Button";
import { Eyebrow } from "@/ui/Eyebrow";
import { NotePad } from "@/ui/NotePad";
import { press } from "@/ui/press";
import { Skeleton, SkeletonBlock } from "@/ui/Skeleton";
import { SourceFailure } from "@/ui/SourceFailure";
import { StickyNote } from "@/ui/StickyNote";
import { Zone } from "@/ui/Zone";
import { noteStampText } from "../stamp";
type Expiry = AccountSettings["noteExpiry"];
type NoteBoardProps = {
  state: ZoneState<BoardNote>;
  retryIn: number;
  onRetry: () => void;
  expiry: Expiry;
  now: Date;
  clock: Clock;
  tz: string;
  breakpoint: Breakpoint;
  onOpenNote: (id: string) => void;
  onAddNote: () => void;
  xstyle?: stylex.StyleXStyles;
};
const SKELETON_NOTES = [0, 1, 2];
const styles = {
  root: {
    kAzted: "x1us19tq",
    kWkggS: "xh540kj",
    kaIpWk: "xwe7p7i",
    kmVPX3: "x1cytmdl",
    kB7OPa: "x9f619",
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kOIVth: "xf5y8le",
    k7Eaqz: "xeuugli",
    $$css: true
  },
  grid: {
    k1xSpc: "xrvj5dj",
    kumcoG: "x12jgyjy",
    kOIVth: "x1k38wn4",
    kLKAdn: "x1q88djf",
    kg3NbH: "xaope02",
    kGO01o: "x29khev",
    $$css: true
  },
  note: {
    k1xSpc: "x1lliihq",
    kzqmXN: "xh8yej3",
    kogj98: "x1ghz6dp",
    kmVPX3: "x1717udv",
    kMzoRj: "xc342km",
    kWkggS: "xjbqb8w",
    kVVagm: "xln7xf2",
    kMwMTN: "x1heor9g",
    k9WMMc: "x1yc453h",
    kkrTdU: "x1ypdohk",
    kaIpWk: "x1wz39yh",
    $$css: true
  }
};
export function NoteBoard({
  state,
  retryIn,
  onRetry,
  expiry,
  now,
  clock,
  tz,
  breakpoint,
  onOpenNote,
  onAddNote,
  xstyle
}: NoteBoardProps) {
  const large = breakpoint === "large";
  return <section {...stylex.props(styles.root, xstyle)}>
      <div className="x78zum5 x1a02dak x1qughib xuk3077 xddi9bj">
        <div className="x78zum5 xdt5ytf x152posx">
          <Eyebrow>{countFor(state, expiry, clock, tz)}</Eyebrow>
          <h2 className="x1ghz6dp x4c74qz x1v94iar x75m3dq xo5v014">{copy.notes.board}</h2>
        </div>
        <Button icon={SquarePen} size={large ? "lg" : "md"} onClick={onAddNote}>
          {copy.notes.addNote}
        </Button>
      </div>
      <Zone state={state} loading={<BoardSkeleton />} empty={<EmptyBoard onAddNote={onAddNote} />} failed={<SourceFailure icon={StickyNoteGlyph} title={copy.notes.unavailable} body={copy.zone.failedBody(retryIn)} retryLabel={copy.zone.retry} onRetry={onRetry} />}>
        {notes => <div className="xrvj5dj x12jgyjy x1k38wn4 x1q88djf xaope02 x29khev">
            {notes.map(note => {
          const time = noteStampText(note, now, clock, tz);
          return <button key={note.id} type="button" aria-label={copy.notes.noteName(note.author, time, note.text)} onClick={() => onOpenNote(note.id)} {...stylex.props(styles.note, press.scale)}>
                  <StickyNote rotate={note.rotation} author={note.author} time={time} width="100%" minHeight={large ? 280 : 200}>
                    <span className="x14md6fn xwn7fz2 xj0a0fe">{boardText(note.text)}</span>
                  </StickyNote>
                </button>;
        })}
          </div>}
      </Zone>
    </section>;
}
function countFor(state: ZoneState<BoardNote>, expiry: Expiry, clock: Clock, tz: string): string {
  if (state.kind === "loading") return copy.notes.loadingCount;
  if (state.kind === "failed") return copy.notes.failedCount;
  if (state.kind === "empty") return copy.notes.count(0, expiry);
  const count = copy.notes.count(state.data.length, expiry);
  if (state.updatedAt === undefined) return count;
  return copy.zone.updated(count, clockAt(state.updatedAt, clock, tz));
}
function BoardSkeleton() {
  return <Skeleton label={copy.notes.loading} xstyle={styles.grid}>
      {SKELETON_NOTES.map(note => <SkeletonBlock key={note} height={200} radius={radii.sm} />)}
    </Skeleton>;
}
function EmptyBoard({
  onAddNote
}: {
  onAddNote: () => void;
}) {
  return <div className="x1iyjqo2 x13hwc6b xvndefy xbsl7fq x1ky62hi xwe7p7i xpwvm28 x9f619 x78zum5 x1a02dak x6s0dn4 xc26acl xl56j7k x1pi5268">
      <NotePad size="lg" label={copy.notes.addFirst} onClick={onAddNote} />
      <div className="x1c4vz4f xs83m0k xtaqc9j x1klvvdw x78zum5 xdt5ytf x883omv">
        <span className="xuaujqh x1v94iar xipi2l9 x1159mfc">{copy.notes.emptyTitle}</span>
        <span className="xs97afq x15i68ds xfrs9s4 x27kpxv x1fzhlzt">{copy.notes.emptyBody}</span>
      </div>
    </div>;
}